import CalendarImage from "@assets/icons/calendar.tsx";
import { useBackground } from "@components/BackfroundContext";
import classNames from "classnames";
import React, { useState, useRef, useEffect } from "react";
import { initializeCalendarData } from "src/utils/initialCalendarData";
import { CalendarModalSingleDate } from "./calendar/CalendarModalSingleDate";
import styles from "./styles.module.scss";

interface SingleCalendarButton {
  onChange: (value: Date) => void;
}

const SingleCalendarButton: React.FC<SingleCalendarButton> = ({ onChange }) => {
  const { setIsBlurred } = useBackground();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { today } = initializeCalendarData();
  const [singleDate, setSingleDate] = useState<Date>(today);
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        isCalendarOpen &&
        dialogRef.current &&
        !dialogRef.current.contains(e.target as Node)
      ) {
        setIsCalendarOpen(false);
        setIsBlurred(false);
        dialogRef.current.close();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [isCalendarOpen, setIsBlurred]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isCalendarOpen) {
        setIsCalendarOpen(false);
        setIsBlurred(false);
        dialogRef.current?.close();
      }
    };

    if (isCalendarOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isCalendarOpen, setIsBlurred]);

  const toggleCalendar = (
    e:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (
      e.type === "click" ||
      (e as React.KeyboardEvent).key === "Enter" ||
      (e as React.KeyboardEvent).key === " "
    ) {
      e.stopPropagation();
      setIsCalendarOpen((prev) => {
        setIsBlurred(!prev);
        return !prev;
      });
      if (!isCalendarOpen) {
        dialogRef.current?.showModal();
      }
    }
  };

  const handleDateChange = (date: Date) => {
    setSingleDate(date);
    onChange(date);
    setIsCalendarOpen(false);
    setIsBlurred(false);
    dialogRef.current?.close();
  };

  return (
    <div className={styles.calendarWrapper}>
      <div className={styles.bg} />
      <button
        type="button"
        onClick={toggleCalendar}
        onKeyDown={toggleCalendar}
        className={classNames(styles.element, styles.calendarButton, {
          [styles["element--active"]]: isCalendarOpen,
        })}
      >
        <CalendarImage color="gray" />
        <p className={styles.tabItem}>{singleDate.toLocaleDateString()}</p>
      </button>

      <dialog ref={dialogRef} className={styles.modalContainer}>
        <CalendarModalSingleDate
          closeModal={() => {
            setIsCalendarOpen(false);
            setIsBlurred(false);
            dialogRef.current?.close();
          }}
          onDateChange={handleDateChange}
        />
      </dialog>
    </div>
  );
};

export default SingleCalendarButton;
